import { APP_BASE_HREF, isPlatformServer } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DOCUMENT, Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
import { catchError, lastValueFrom, Observable, throwError } from 'rxjs';
import { PaymentRequestService } from '@ng-web-apis/payment-request';
import { EncripteService } from '../Interceptor/httpProvider';
import { AuthTokenManagerService } from './auth-token-manager.service';

@Injectable({
  providedIn: 'root',
})
export class ProviderService {
  private baseURL: string;
  private isServer: boolean;

  constructor(
    private readonly injector: Injector,
    @Inject(PLATFORM_ID) platformId: object,
    @Inject(DOCUMENT) private document: Document,
    @Inject(APP_BASE_HREF) private baseHref: string,
    private readonly paymentRequest: PaymentRequestService,
    private http: HttpClient,
    private encripteService: EncripteService,
    private authTokenManager: AuthTokenManagerService
  ) {
    this.isServer = isPlatformServer(platformId);
    this.baseURL = this.isServer ? this.baseHref : `${this.document.location.origin}/`;
  }

  async retrieveSessionData(id: string, service_mean: string): Promise<any> {
    if (
      service_mean === 'card' ||
      service_mean === 'apple_pay' ||
      service_mean === 'google_pay' ||
      service_mean === 'botim'
    ) {
      const e = await this.sec(id);
      return this.getData(id, e.aut);
    } else {
      return this.initSession(id, service_mean, null);
    }
  }

  getIban(sessionId: string): Promise<any> {
    const url = this.baseURL + 'api/iban/' + sessionId;
    return new Promise((res, rej) => {
      this.http
        .get(url, {
          headers: {
            'Content-Type': 'application/json',
          },
          responseType: 'json',
        })
        .pipe(
          catchError(async err => {
            rej(err);
          })
        )
        .subscribe(data => {
          res(data);
        });
    });
  }

  private getHeaders(auth?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    if (auth) {
      headers = headers.set('Authorization', auth);
    }
    return headers;
  }

  private handleError(error: any): any {
    return throwError(() => error);
  }

  streamSessionStatus(id: string, service_mean: string) {
    console.log(
      'streamSessionStatus',
      `${this.baseURL}api/${id}/status/stream/v2?service_mean=${service_mean}`
    );
    return new Observable(observer => {
      const eventSource = new EventSource(
        `${this.baseURL}api/${id}/status/stream/v2?service_mean=${service_mean}`
      );

      eventSource.onmessage = event => {
        observer.next(event.data);
      };

      eventSource.onerror = error => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  streamSkrillSessionStatus(id: string, service_mean: string) {
    return new Observable(observer => {
      const eventSource = new EventSource(
        `${this.baseURL}api/${id}/status/stream/v2?service_mean=${service_mean}`
      );

      eventSource.onmessage = event => {
        observer.next(event.data);
      };

      eventSource.onerror = error => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  streamNetellerHandleStatus(id: string) {
    return new Observable(observer => {
      const eventSource = new EventSource(
        `${this.baseURL}api/${id}/status/stream/neteller?service_mean=neteller`
      );

      eventSource.onmessage = event => {
        observer.next(event.data);
      };

      eventSource.onerror = error => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  streamMpesaSessionStatus(id: string, service_mean: string) {
    return new Observable(observer => {
      const eventSource = new EventSource(
        `${this.baseURL}api/${id}/status/stream/mpesa?service_mean=${service_mean}`
      );

      eventSource.onmessage = event => {
        observer.next(event.data);
      };

      eventSource.onerror = error => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  private postRequest(url: string, body: any, headers?: HttpHeaders): Promise<any> {
    return this.http
      .post(url, body, { headers, responseType: 'json' })
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  private getRequest(url: string, headers?: HttpHeaders): Promise<any> {
    return this.http
      .get(url, { headers, responseType: 'json' })
      .pipe(catchError(this.handleError))
      .toPromise();
  }

  async sec(id: string): Promise<any> {
    const url = `${this.baseURL}z/x/${id}`;
    return this.postRequest(url, {});
  }

  async getData(id: string, auth: string): Promise<any> {
    const url = `${this.baseURL}api/status/${id}`;
    const headers = this.getHeaders(auth);
    return this.postRequest(url, {}, headers);
  }

  async initSession(id: string, provider: string, currency: string | null = null): Promise<any> {
    const url = `${this.baseURL}api/initSession/${id}`;
    const body = { provider, currency };
    return this.postRequest(url, body);
  }

  async updateSession(
    id: string,
    provider: string,
    token: string | null,
    idCard: string | null,
    riskjs_device_session_id: string | null = null,
    displayName: string | null = null
  ): Promise<any> {
    const url = `${this.baseURL}api/updatesession/${id}`;
    const body = {
      id,
      provider,
      token,
      id_card: idCard,
      riskjs_device_session_id,
      card_display_name: displayName,
    };
    return this.postRequest(url, body);
  }

  async updateCryptoSession(id: string, service_mean: string, data: any): Promise<any> {
    const url = `${this.baseURL}api/updatesession/${id}`;
    const body = {
      id,
      customer_additional_fields: data['customer_additional_fields'],
    };
    return this.postRequest(url, body);
  }

  async getSession(id: string): Promise<any> {
    const url = `${this.baseURL}api/getSession/${id}`;
    return this.getRequest(url);
  }

  async getCustomerBanks(id: string): Promise<any> {
    const url = `${this.baseURL}api/${id}/bank`;
    return this.getRequest(url);
  }

  async getSessionStatus(id: string): Promise<any> {
    const url = `${this.baseURL}api/${id}/status`;
    return this.getRequest(url);
  }

  async disconnectFromBank(id: string, bank_identifier: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/${id}/deleteBank/${bank_identifier}`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, {}, headers);
    });
  }

  async sendPaymentRequest(id: string, bank_identifier: string) {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/${id}/pay/intent/${bank_identifier}`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, {}, headers);
    });
  }

  async neoBankCreatePayout(id: string, payoutDetails: any): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const { entity_id, iban, payout_type, save_iban } = payoutDetails;
      const url = `${this.baseURL}api/${id}/payout`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(
        url,
        {
          entity_id,
          iban,
          payout_type,
          save_iban,
        },
        headers
      );
    });
  }

  async getGtrk(): Promise<any> {
    const url = `${this.baseURL}api/gtrk`;
    return this.postRequest(url, {});
  }

  async validateSession(
    id: string,
    provider: string,
    token: string | null,
    device: any | null,
    native: boolean = false
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/3ds/${id}`;
      const body = { provider, token, device, native };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async makePayment(
    id: string,
    provider: string,
    token: string | null,
    is_pci: boolean
  ): Promise<any> {
    if (is_pci) {
      return await this.finalizePayment(id);
    } else {
      const url = `${this.baseURL}api/cfpay/${id}`;
      const body = { provider, token };
      return this.postRequest(url, body);
    }
  }

  async check3DSEnrollment(
    id: string,
    provider: string,
    token: string | null,
    vtoken: any,
    saveCard: boolean,
    dcc_uptake: string = 'NOT_AVAILABLE',
    native = false
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/check-3ds-enrollment/${id}`;
      const body = { provider, token, vtoken, saveCard, dcc_uptake, native };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async validateMerchant(id: string, provider: string, token: string | null): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/validateMerchant/${id}`;
      const body = { provider, urlTovalidate: token };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async bindGooglePayGatewayToken(id: string, token: string, scheme: string, last4: string) {
    const url = `${this.baseURL}api/google-pay/gateway-token/${id}`;
    return this.postRequest(url, { token, scheme, last4 });
  }

  async validateMerchantV2(
    id: string,
    provider: string,
    token: string | null,
    merchant_id: string
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/validateMerchantV2/${id}`;
      const body = { provider, urlTovalidate: token, merchant_id };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async createRecurringPlan(
    id: string,
    card_id: string,
    amount: number,
    currency: string | null,
    periodicity: string,
    start_date: number | null,
    expiry_date: number | null,
    repeat_day: number | null,
    is_confirmed: boolean = false
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/create-recurring-plan/${id}`;
      const body = {
        card_id,
        amount,
        currency,
        periodicity,
        start_date,
        expiry_date,
        repeat_day,
        is_confirmed,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async getCryptoTransactionStatus(id: string): Promise<any> {
    const url = `${this.baseURL}api/${id}/status2?rqt=${Date.now()}`;
    return this.getRequest(url);
  }

  async getAgents(id: string): Promise<any> {
    const url = `${this.baseURL}api/agent/${id}`;
    return this.postRequest(url, {});
  }

  async getCountries(id: string): Promise<any> {
    const url = `${this.baseURL}api/country/${id}`;
    return this.getRequest(url);
  }

  async getCities(id: string, country_code: any): Promise<any> {
    const url = `${this.baseURL}api/session/${id}/city?country_code=${country_code}`;
    return this.getRequest(url);
  }

  async deleteCard(id: string, cardId: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/deleteCard/${id}?timestamp=${Date.now()}`;
      const body = { card_id: cardId };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async deleteEmail(id: string, emailId: string, mop: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/deleteEmail/${id}?timestamp=${Date.now()}`;
      const body = { email_id: emailId, mop };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async deleteCryptoAddress(id: string, address: string, network: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/deleteCryptoAddress/${id}`;
      const body = { crypto_address: address, crypto_network: network };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async cryptoRefund(
    id: string,
    cryptoAddress: string,
    dest_tag: string,
    currencyCode: string,
    networkCode: string,
    saveWallet: boolean,
    walletType: any,
    selectedVasp: any
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/crypto/refund/${id}`;
      const travelRule = {
        wallet_type: walletType,
        vasp: {
          id: selectedVasp?.id,
          did: selectedVasp?.did,
          name: selectedVasp?.name,
        },
      };
      const body = {
        crypto_address: cryptoAddress,
        crypto_currency: currencyCode,
        crypto_destination_tag: dest_tag,
        network: networkCode,
        save_crypto_wallet: saveWallet,
        crypto_travel_rule: travelRule,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async getCryptoAddress(id: string): Promise<any[]> {
    const url = `${this.baseURL}api/getCryptoAdress/${id}`;
    return this.getRequest(url);
  }

  pay(details: PaymentDetailsInit): void {
    this.paymentRequest
      .request(details)
      .then(response => response.complete())
      .catch(error => console.error('Payment error:', error));
  }

  async doPaymentWithCash(id: string, agent: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/cash/${id}`;
      const body = { id, agent };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async doRefundWithCash(Id: any, agent: any, phoneNumberId?: string) {
    return this.authTokenManager.executeWithAuth(Id, this.baseURL, async token => {
      const url = `${this.baseURL}api/cash/refund/${Id}`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(
        url,
        {
          id: Id,
          agent: agent,
          ...(phoneNumberId ? { phone_number_id: phoneNumberId } : {}),
        },
        headers
      );
    });
  }

  //refund by bank

  async doRefundWithBank(
    Id: any,
    iban: any,
    save_iban: any = true,
    beneficiary_name: any,
    beneficiary_address: any,
    beneficiary_country_code: any,
    beneficiary_city_code: any,
    bic: any
  ) {
    return this.authTokenManager.executeWithAuth(Id, this.baseURL, async token => {
      const url = `${this.baseURL}api/bank/refund/${Id}`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(
        url,
        {
          id: Id,
          iban: iban,
          save_iban: save_iban,
          beneficiary_name: beneficiary_name,
          beneficiary_address: beneficiary_address,
          beneficiary_country_code: beneficiary_country_code,
          beneficiary_city_code: beneficiary_city_code,
          bic: bic,
        },
        headers
      );
    });
  }

  removeIban(id: any, id1: any) {
    return this.authTokenManager.executeWithAuth(id, this.baseURL, async token => {
      const url = `${this.baseURL}api/deleteIban/${id}`;
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(
        url,
        {
          id: id,
          iban: id1,
        },
        headers
      );
    });
  }

  // Process mobile payment
  processMobilePayment(
    sessionId: string,
    phoneNumber: string,
    channel: string | undefined,
    savePhoneNumber: boolean
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/mobile-money/pay/${sessionId}`;

      const body = {
        phone_number: phoneNumber,
        selected_channel: channel,
        save_phone_number: savePhoneNumber,
      };

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });

      return this.postRequest(url, body, headers);
    });
  }

  // Process mobile refund
  async processMobileRefund(
    sessionId: string,
    PhoneNumber: string,
    channel?: string,
    savePhoneNumber = false
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/mobile-money/refund/${sessionId}`;

      const body = {
        customer_phone: PhoneNumber,
        selected_channel: channel,
        save_phone_number: savePhoneNumber,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async initiatePayment(id: string, save_card: boolean, dcc_uptake: any): Promise<any> {
    const url = `${this.baseURL}api/initiate-payment`;
    const body = { id, save_card, dcc_uptake };
    return this.postRequest(url, body);
  }

  async gatetopayCashout(id: string, fields: any) {
    const url = `${this.baseURL}api/gatetopay-cashout`;
    const body = { id, card: fields };
    return this.postRequest(url, body);
  }

  async gatetopayCashin(id: string, fields: any) {
    const url = `${this.baseURL}api/gatetopay-cashin`;
    const body = { id, card: fields };
    return this.postRequest(url, body);
  }

  async authenticatePayer(
    id: string,
    provider: string,
    device: any,
    complete_issuer_fingerprint = 'U',
    native = false
  ): Promise<any> {
    const url = `${this.baseURL}api/authenticate-payer`;
    const body = { id, provider, device, complete_issuer_fingerprint, native };
    return this.postRequest(url, body);
  }

  async createSkrillSession(sessionId: any, paymentData: any) {
    const url = `${this.baseURL}api/checkout-skrill/${sessionId}`;
    const body = paymentData;
    return this.postRequest(url, body);
  }

  async createNetellerPayment(sessionId: any, paymentData: any) {
    const url = `${this.baseURL}api/pay-neteller/${sessionId}`;
    const body = paymentData;
    return this.postRequest(url, body);
  }

  async createNetellerRefund(sessionId: any, paymentData: any) {
    const url = `${this.baseURL}api/refund-neteller/${sessionId}`;
    const body = paymentData;
    return this.postRequest(url, body);
  }

  async createNetellerHandle(sessionId: any, paymentData: any) {
    const url = `${this.baseURL}api/checkout-neteller/${sessionId}`;
    const body = paymentData;
    return this.postRequest(url, body);
  }

  async finalizePayment(id: string): Promise<any> {
    const url = `${this.baseURL}api/finalize-payment`;
    const body = { id };
    return this.postRequest(url, body);
  }

  async finalizePayment_v2(session_id: string, gateway_recommendation = 'PROCEED') {
    const url = `${this.baseURL}api/finalize-payment-v2`;
    const body = { session_id, gateway_recommendation };
    return this.postRequest(url, body);
  }

  async payWithApple(
    session_id: string,
    token: string | null = null,
    display_name = null,
    scheme = null
  ) {
    const url = `${this.baseURL}api/sdk/session/${session_id}/pay`;
    return this.postRequest(url, { token: token, display_name: display_name, scheme });
  }

  getAccountsForBank(sessionId: string, entity_id: string) {
    const url = `${this.baseURL}api/${sessionId}/account/${entity_id}`;
    return this.getRequest(url);
  }

  getMopsForRetailer(sessionId: string) {
    const url = `${this.baseURL}api/${sessionId}/mop`;
    return this.getRequest(url);
  }

  getDcc(sessionID: string) {
    const url = `${this.baseURL}api/${sessionID}/payment-options-inquiry`;
    return this.getRequest(url);
  }

  applyDynamicCss(cssUrlPath: string): void {
    try {
      const linkElement = this.document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.type = 'text/css';
      linkElement.href = cssUrlPath;

      this.document.head.appendChild(linkElement);
      console.log('CSS styles link added successfully');
    } catch (error) {
      console.error('Error applying CSS styles:', error);
    }
  }

  async processCardRefund(
    sessionId: string,
    cardId: string,
    deviceSessionId: string | null = null
  ) {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/${sessionId}/card/refund`;
      const destination = {
        type: 'card_id',
        value: cardId,
      };
      const body = { destination, device_session_id: deviceSessionId };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async getParticipants(sessionId: string): Promise<any> {
    const url = `${this.baseURL}api/${sessionId}/participant`;
    return this.getRequest(url);
  }

  async doPaymentWithDomesticBank(
    sessionId: string,
    bicCode: string,
    aliasType: string,
    aliasValue: string,
    otpValue: string
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/domestic-bank/${sessionId}`;
      const body = { sessionId, bicCode, aliasType, aliasValue, otpValue };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  private patchRequest(url: string, body: any, headers?: HttpHeaders): Promise<any> {
    return lastValueFrom(
      this.http
        .patch(url, body, { headers, responseType: 'json' })
        .pipe(catchError(this.handleError))
    );
  }
  async updateSessionwithCardData(sessionId: string, cardData: any) {
    const url = `${this.baseURL}api/sdk/session/${sessionId}`;
    const body = {
      card: {
        ...(cardData.nameOnCard && { name_on_card: cardData.nameOnCard }),
        ...(cardData.number && { number: cardData.number }),
        ...(cardData.cvv && { cvv: cardData.cvv }),
        ...(cardData.expiryMonth && { expiry_date_mm: cardData.expiryMonth }),
        ...(cardData.expiryYear && { expiry_date_yy: cardData.expiryYear }),
      },
    };
    return this.patchRequest(url, body);
  }

  async domesticSolutionRefund(sessionId: string, data: any, saveBeneficiary: any): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/domestic-solution/refund/${sessionId}`;
      const body = {
        ...data,
        save_beneficiary: saveBeneficiary,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async domesticSolutionPay(sessionId: string, data: any): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/domestic-solution/pay/${sessionId}`;
      const body = { ...data };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async getBeneficiaryDetails(sessionId: string) {
    const url = `${this.baseURL}api/${sessionId}/beneficiary`;
    return this.getRequest(url);
  }

  async deleteBeneficiary(sessionId: string, beneficiaryId: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/deleteBeneficiary/${sessionId}`;
      const body = { beneficiary_id: beneficiaryId };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async doMpesaPay(sessionId: any, customerPhone: any): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/mpesa/c2b/${sessionId}`;
      const body = {
        customer_phone: customerPhone,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async doMpesaRefund(sessionId: any, destination: any): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/mpesa/b2c/${sessionId}`;
      const body = {
        destination: destination,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async doBinancePayRefund(sessionId: any, binanceUserID: any, saveBinanceUser: any): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/binance-pay/refund/${sessionId}`;
      const body = {
        session_id: sessionId,
        binance_user_id: binanceUserID,
        save_binance_user: saveBinanceUser,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  streamMpesaGetSession(id: string) {
    return new Observable(observer => {
      const eventSource = new EventSource(`${this.baseURL}api/${id}/mpesa/session`);

      eventSource.onmessage = event => {
        observer.next(event.data);
      };

      eventSource.onerror = error => {
        observer.error(error);
        eventSource.close();
      };

      return () => eventSource.close();
    });
  }

  async deletePhoneNumber(sessionId: string, phoneNumber: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/deletePhoneNumber/${sessionId}`;
      const body = { phone_number: phoneNumber };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async processSMPayPayment(code: string, state: string, sessionId: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/sm_pay/pay/${sessionId}`;
      const body = {
        state: state,
        code: code,
      };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  streamGetSessionStatus(
    id: string,
    field?: string,
    interval_ms: number = 3000,
    maxAttempts: number = 20
  ): Observable<any> {
    return new Observable(observer => {
      let attempt = 0;

      const poll = async () => {
        attempt++;

        try {
          const params = new URLSearchParams();
          if (field) params.set('field', field);

          const url = `${this.baseURL}api/session/${id}/status?${params.toString()}`;
          const response = await this.getRequest(url); // your existing request helper
          observer.next(response); // emit the current status

          const { status_name } = response;

          if (['Expired'].includes(status_name) || attempt >= maxAttempts) {
            observer.complete();
          } else {
            setTimeout(poll, interval_ms);
          }
        } catch (err) {
          observer.error(err);
        }
      };

      poll(); // start polling
    });
  }

  getCurrentSessionStatus(id: string, field?: string): Observable<any> {
    return new Observable(observer => {
      const params = new URLSearchParams();
      if (field) params.set('field', field);

      const url = `${this.baseURL}api/session/${id}/status?${params.toString()}`;

      this.getRequest(url)
        .then(response => {
          observer.next(response);
          observer.complete();
        })
        .catch(err => {
          observer.error(err);
        });
    });
  }

  /**
   * Clear authentication token for a specific session
   * Call this when user navigates away or session ends
   */
  clearSessionToken(sessionId: string): void {
    this.authTokenManager.clearToken(sessionId);
  }

  /**
   * Clear all authentication tokens
   * Call this on logout or app cleanup
   */
  clearAllTokens(): void {
    this.authTokenManager.clearAllTokens();
  }

  async deletePhoneById(sessionId: string, phoneId: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/mobile-money/delete/${sessionId}`;
      const body = { phone_id: phoneId };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async deleteBotimWallet(sessionId: string, phoneNumber: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/botim/wallet/${sessionId}`;
      const body = { phone_number: phoneNumber };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  async payWithBotim(id: string, type: 'inapp' | 'qrcode'): Promise<any> {
    const url = `${this.baseURL}api/botim/pay/${id}`;
    const body = { type };
    return this.postRequest(url, body);
  }

  async doBotimRefund(
    sessionId: string,
    phoneNumber: string,
    saveDestination: boolean = true
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async token => {
      const url = `${this.baseURL}api/botim/refund/${sessionId}`;
      const body = { phone_number: phoneNumber, save_destination: saveDestination };
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: token,
      });
      return this.postRequest(url, body, headers);
    });
  }

  authorizeMobilePayment(
    sessionId: string,
    otp: string | null,
    resend_otp: boolean = false
  ): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/mobile-money/pay/authorize/${sessionId}`;

      const body = { otp, resend_otp };

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });

      return this.postRequest(url, body, headers);
    });
  }

  triggerPaymentPrompt(sessionId: string): Promise<any> {
    return this.authTokenManager.executeWithAuth(sessionId, this.baseURL, async authToken => {
      const url = `${this.baseURL}api/${sessionId}/pay/trigger-sim-toolkit`;

      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: authToken,
      });

      return this.postRequest(url, {}, headers);
    });
  }
}
